import { TZDate } from '@date-fns/tz';
import { useRef } from 'react';

import { formatDate } from '@dynatrace-sdk/units';

import { useFormFieldContext } from '../../forms-core/hooks/useFormFieldContext.js';
import { useFormFieldMessagesContext } from '../../forms-core/hooks/useFormFieldMessagesContext.js';
import { field as yearFieldStyle } from '../Calendar.sty.js';
import { useYear } from '../hooks/useYear.js';
import { CalendarItem } from './CalendarItem.js';

/** @internal */
export const Year = ({ date }: { date: TZDate }) => {
  // Reference of the year button, needed to pass to the hook for proper focus management
  const itemRef = useRef<HTMLDivElement>(null);

  const { ariaLabel, tabIndex, isCurrent, isSelected, onClick, isDisabled } =
    useYear({
      date,
      itemRef,
    });
  const { disabled, ariaDisabled } = useFormFieldContext();
  const {
    state: { showError },
  } = useFormFieldMessagesContext();
  const highlightColor = isSelected ? 'primary' : 'neutral';
  const validatedHighlightColor =
    showError && highlightColor === 'primary' ? 'critical' : highlightColor;
  const yearValue = formatDate(date, { year: 'numeric' });

  return (
    <td
      role="presentation"
      className={yearFieldStyle({
        color: validatedHighlightColor,
        isDisabled: isDisabled || disabled || ariaDisabled,
      })}
    >
      <CalendarItem
        ref={itemRef}
        className="calendar-button calendar-body-button"
        isCurrent={isCurrent}
        isSelected={isSelected}
        isDisabled={isDisabled || disabled}
        highlightColor={validatedHighlightColor}
        tabIndex={tabIndex}
        role="gridcell"
        aria-selected={!!isSelected}
        ariaLabel={ariaLabel}
        onClick={onClick}
        view="year"
        data-dt-children-name="SelectYear"
        data-dt-properties={`value:${yearValue}`}
      >
        {yearValue}
      </CalendarItem>
    </td>
  );
};

Year['displayName'] = 'Year';
